"use client";

import { useEffect, useState, useRef, useCallback } from "react";
import { ChevronUp, ChevronDown, Pin, PinOff, Move } from "lucide-react";
import { cn } from "@/app/lib/utils";
import { Button } from "./button";
import { safeLocalStorage } from "@/app/lib/storage";
import { STORAGE_KEYS } from "@/app/lib/enums";

interface ScrollButtonProps {
   containerRef?: React.RefObject<HTMLElement | null>;
   offset?: number;
   className?: string;
   hideDelay?: number;
   topButtonClassName?: string;
   bottomButtonClassName?: string;
}

export const ScrollButton = ({
   containerRef,
   offset = 100,
   className = "rounded-full w-10 h-10",
   hideDelay = 3000,
   topButtonClassName = "",
   bottomButtonClassName = ""
}: ScrollButtonProps) => {
   const [scrollPosition, setScrollPosition] = useState<"top" | "middle" | "bottom">(
      "top"
   );
   const [isVisible, setIsVisible] = useState(false);
   const [isPinned, setIsPinned] = useState(false);
   const [isDragging, setIsDragging] = useState(false);
   const [position, setPosition] = useState({ x: 0, y: 0 });
   const [hoveredButton, setHoveredButton] = useState<
      "top" | "bottom" | "pin" | "move" | null
   >(null);
   const [userHasScrolled, setUserHasScrolled] = useState(false);
   const dragContainerRef = useRef<HTMLDivElement>(null);
   const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
   const dragStartPos = useRef({ x: 0, y: 0 });

   // Initialize pin state and position from localStorage
   useEffect(() => {
      const savedPin = safeLocalStorage.getItem(STORAGE_KEYS.SCROLL_BUTTON_PINNED);
      if (savedPin === "true") {
         setIsPinned(true);
         setIsVisible(true);
      }

      const savedPosition = safeLocalStorage.getItem(STORAGE_KEYS.SCROLL_BUTTON_POSITION);
      if (savedPosition) {
         try {
            const pos = JSON.parse(savedPosition);
            if (typeof pos.x === "number" && typeof pos.y === "number") {
               setPosition(pos);
            }
         } catch (e) {
            console.error("Failed to parse saved position", e);
         }
      }
   }, []);

   useEffect(() => {
      if (position.x !== 0 || position.y !== 0) {
         safeLocalStorage.setItem(
            STORAGE_KEYS.SCROLL_BUTTON_POSITION,
            JSON.stringify(position)
         );
      }
   }, [position]);

   const showButtons = useCallback(() => {
      setIsVisible(true);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      if (!hoveredButton && !isPinned) {
         scrollTimeoutRef.current = setTimeout(() => {
            setIsVisible(false);
         }, hideDelay);
      }
   }, [hideDelay, hoveredButton, isPinned]);

   const togglePin = () => {
      const newPinState = !isPinned;
      setIsPinned(newPinState);
      safeLocalStorage.setItem(STORAGE_KEYS.SCROLL_BUTTON_PINNED, newPinState.toString());
      if (newPinState) {
         setIsVisible(true);
         if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
         }
      } else {
         showButtons();
      }
   };

   useEffect(() => {
      const handleScroll = () => {
         const element = containerRef?.current;
         const viewportHeight = element?.clientHeight ?? window.innerHeight;
         const fullHeight =
            element?.scrollHeight ?? document.documentElement.scrollHeight;
         if (fullHeight <= viewportHeight) {
            setIsVisible(false);
            return;
         }

         const scrollPos = element?.scrollTop ?? window.scrollY;
         const fromTop = scrollPos;
         const fromBottom = fullHeight - (scrollPos + viewportHeight);

         // Determine scroll position
         if (fromTop < offset) {
            setScrollPosition("top");
         } else if (fromBottom < offset) {
            setScrollPosition("bottom");
         } else {
            setScrollPosition("middle");
         }

         // Only show buttons when user has manually scrolled
         if (!userHasScrolled) {
            setUserHasScrolled(true);
         }

         showButtons();
      };

      const target = containerRef?.current || window;
      target.addEventListener("scroll", handleScroll, { passive: true });

      // Initialize scroll positions without showing buttons
      const initScrollPositions = () => {
         const element = containerRef?.current;
         const viewportHeight = element?.clientHeight ?? window.innerHeight;
         const fullHeight =
            element?.scrollHeight ?? document.documentElement.scrollHeight;

         if (fullHeight <= viewportHeight) return;

         const scrollPos = element?.scrollTop ?? window.scrollY;
         const fromTop = scrollPos;
         const fromBottom = fullHeight - (scrollPos + viewportHeight);

         // Determine initial scroll position
         if (fromTop < offset) {
            setScrollPosition("top");
         } else if (fromBottom < offset) {
            setScrollPosition("bottom");
         } else {
            setScrollPosition("middle");
         }
      };

      requestAnimationFrame(initScrollPositions);

      return () => {
         target.removeEventListener("scroll", handleScroll);
         if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      };
   }, [containerRef, offset, hideDelay, showButtons]);

   const scrollToTop = () => {
      const scrollElement = containerRef?.current;
      const scrollOptions = {
         top: 0,
         behavior: "smooth" as const
      };

      if (scrollElement) {
         scrollElement.scrollTo(scrollOptions);
      } else {
         window.scrollTo(scrollOptions);
      }
   };

   const scrollToBottom = () => {
      const scrollElement = containerRef?.current;
      const scrollHeight = scrollElement
         ? scrollElement.scrollHeight
         : document.documentElement.scrollHeight;

      const scrollOptions = {
         top: scrollHeight,
         behavior: "smooth" as const
      };

      if (scrollElement) {
         scrollElement.scrollTo(scrollOptions);
      } else {
         window.scrollTo(scrollOptions);
      }
   };

   const handleMouseEnter = (button: "top" | "bottom" | "pin" | "move") => {
      setHoveredButton(button);
   };

   const handleMouseLeave = () => {
      setHoveredButton(null);
      showButtons();
   };

   const handleDragStart = (e: React.MouseEvent) => {
      if (!dragContainerRef.current) return;
      setIsDragging(true);
      dragStartPos.current = {
         x: e.clientX - position.x,
         y: e.clientY - position.y
      };
      document.body.style.userSelect = "none";
      document.addEventListener("mousemove", handleDrag);
      document.addEventListener("mouseup", handleDragEnd);
   };

   const handleDrag = useCallback(
      (e: MouseEvent) => {
         if (!isDragging) return;
         const newPosition = {
            x: e.clientX - dragStartPos.current.x,
            y: e.clientY - dragStartPos.current.y
         };
         setPosition(newPosition);
      },
      [isDragging]
   );

   const handleDragEnd = useCallback(() => {
      setIsDragging(false);
      document.body.style.userSelect = "";
      document.removeEventListener("mousemove", handleDrag);
      document.removeEventListener("mouseup", handleDragEnd);
   }, [handleDrag]);

   useEffect(() => {
      return () => {
         document.removeEventListener("mousemove", handleDrag);
         document.removeEventListener("mouseup", handleDragEnd);
      };
   }, [handleDrag, handleDragEnd]);

   const containerStyle: React.CSSProperties = {
      transform: `translate(${position.x}px, ${position.y}px)`,
      cursor: isDragging ? "grabbing" : "auto"
   };

   return (
      <div
         ref={dragContainerRef}
         className={cn(
            "fixed flex flex-col gap-3 right-6 top-1/2 -translate-y-1/2 z-50 transition-opacity duration-200 p-2 rounded-2xl bg-background/70 backdrop-blur-sm shadow-lg",
            {
               "opacity-100": (userHasScrolled && isVisible) || isPinned,
               "opacity-0 pointer-events-none":
                  (!userHasScrolled || !isVisible) && !isPinned
            }
         )}
         style={containerStyle}
         onMouseEnter={() => setIsVisible(true)}
         onMouseLeave={() => !hoveredButton && showButtons()}
      >
         {/* Controls container - visible when hovering over container */}
         <div
            className={cn(
               "absolute -right-7 top-0 flex flex-col gap-2 transition-opacity duration-200",
               {
                  "opacity-100 pointer-events-auto": isVisible || isDragging,
                  "opacity-0 pointer-events-none": !isVisible && !isDragging
               }
            )}
         >
            {/* Move Handle Button */}
            <Button
               variant="ghost"
               size="icon"
               className={cn(
                  "w-6 h-6 rounded-full bg-background/80 backdrop-blur-sm border border-muted hover:bg-primary/10 cursor-grab",
                  {
                     "cursor-grabbing": isDragging
                  }
               )}
               onMouseDown={handleDragStart}
               aria-label="Move scroll buttons"
            >
               <Move className="h-3 w-3 text-muted-foreground" strokeWidth={1} />
            </Button>

            {/* Pin/Unpin Button */}
            <Button
               variant="ghost"
               size="icon"
               className={cn(
                  "w-6 h-6 rounded-full bg-background/80 backdrop-blur-sm border border-muted hover:bg-primary/10",
                  {
                     "text-primary": isPinned,
                     "text-muted-foreground": !isPinned
                  }
               )}
               onClick={togglePin}
               aria-label={isPinned ? "Unpin scroll buttons" : "Pin scroll buttons"}
            >
               {isPinned ? (
                  <PinOff className="h-3 w-3" strokeWidth={1} />
               ) : (
                  <Pin className="h-3 w-3" strokeWidth={1} />
               )}
            </Button>
         </div>
         {/* Scroll to Top Button */}
         <Button
            variant="outline"
            size="icon"
            className={cn(
               className,
               topButtonClassName,
               "transition-all duration-200 bg-background/90 backdrop-blur-sm border border-border shadow-sm hover:bg-primary/15 hover:border-primary/30 hover:scale-110 dark:opacity-80",
               {
                  "opacity-100": scrollPosition !== "top",
                  "opacity-50 hover:opacity-100": scrollPosition !== "top",
                  "opacity-0 pointer-events-none": scrollPosition === "top"
               }
            )}
            onClick={scrollToTop}
            onMouseEnter={() => handleMouseEnter("top")}
            onMouseLeave={handleMouseLeave}
            aria-label="Scroll to top"
         >
            <ChevronUp className="h-6 w-6 text-primary font-bold" />
         </Button>

         {/* Scroll to Bottom Button */}
         <Button
            variant="outline"
            size="icon"
            className={cn(
               className,
               bottomButtonClassName,
               "transition-all duration-200 bg-background/90 backdrop-blur-sm border border-border shadow-sm hover:bg-primary/15 hover:border-primary/30 hover:scale-110 dark:opacity-80",
               {
                  "opacity-100": scrollPosition !== "bottom",
                  "opacity-50 hover:opacity-100": scrollPosition !== "bottom",
                  "opacity-0 pointer-events-none": scrollPosition === "bottom"
               }
            )}
            onClick={scrollToBottom}
            onMouseEnter={() => handleMouseEnter("bottom")}
            onMouseLeave={handleMouseLeave}
            aria-label="Scroll to bottom"
         >
            <ChevronDown className="h-6 w-6 text-primary font-bold" />
         </Button>
      </div>
   );
};
